export function findFirstTreeNode(nodes = [], predicate = () => false) {
  if (!Array.isArray(nodes)) return null;
  for (const node of nodes) {
    if (!node) continue;
    if (predicate(node)) return node;
    const nested = findFirstTreeNode(node.children || [], predicate);
    if (nested) return nested;
  }
  return null;
}

export function treeHasNodeType(nodes = [], type) {
  return Boolean(findFirstTreeNode(nodes, (node) => node.type === type));
}

/**
 * @param {object} context
 * @param {boolean} [context.hasSiteSelector]
 * @param {boolean} [context.hasTree]
 * @param {boolean} [context.hasMilestoneInTree]
 * @param {boolean} [context.hasStudyGate]
 * @param {object} [context.actions] Tour UI helpers from isf.jsx
 */
export function buildIsfPageTourSteps(context = {}) {
  const {
    hasSiteSelector = false,
    hasTree = false,
    hasMilestoneInTree = false,
    hasStudyGate = false,
    actions = {},
  } = context;

  const {
    showOverview,
    openStudyInfo,
    selectFirstMilestone,
    selectFirstPlaceholder,
    openUploadDemo,
    closeUploadDemo,
    cleanupTour,
  } = actions;

  const steps = [
    {
      id: "welcome",
      placement: "center",
      title: "Welcome to eISF",
      body: "This guide walks through study phases (gates), site context, the document tree, status matrices, and uploads. Reopen anytime from Guide.",
      onEnter: () => showOverview?.(),
    },
    {
      id: "guide-button",
      target: '[data-tour="isf-page-guide"]',
      placement: "bottom",
      title: "Page guide",
      body: "Use Guide to replay this walkthrough whenever you need a refresher on eISF features.",
    },
    {
      id: "study-actions",
      target: ".page-breadcrumb .actions-trigger",
      placement: "bottom",
      title: "Study actions",
      body: "Change study or apply placeholders from Actions. Multi-site studies use the site controls below the breadcrumb.",
    },
    {
      id: "study-phase",
      target: '[data-tour="isf-study-gate-picker"]',
      placement: "bottom",
      title: "Study phase picker",
      body: "Phase lets you browse all five study gates: Pre-study, Start-up, Conduct, Close-out, and Post-study. The folder tree and milestones reload for the phase you select. Only the current phase (shown as Current) allows uploads, edits, and placeholder changes.",
      when: () => hasStudyGate,
    },
    {
      id: "gate-readonly",
      placement: "center",
      title: "Read-only phase preview",
      body: "When you open a completed or upcoming phase, the page switches to read-only preview. A banner appears in the preview panel and upload, sign, and tree edit actions are disabled. Switch Phase back to Current to continue working.",
      when: () => hasStudyGate,
    },
    {
      id: "site",
      target: '[data-tour="isf-site-selector"]',
      placement: "bottom",
      title: "Clinical site",
      body: "Pick the site you are working on. Folders, expected documents, milestones, and tasks are scoped to the active site and the selected study phase.",
      when: () => hasSiteSelector,
      onEnter: () => showOverview?.(),
    },
    {
      id: "site-overview-btn",
      target: '[data-tour="isf-site-overview-btn"]',
      placement: "bottom",
      title: "Site overview",
      body: "Overview returns you to the site dashboard after drilling into folders, placeholders, or documents.",
      when: () => hasSiteSelector,
      onEnter: () => showOverview?.(),
    },
    {
      id: "site-kpis",
      target: '[data-tour="isf-site-overview-kpis"]',
      placement: "left",
      title: "Site status matrix",
      body: "These KPI tiles summarise site-level progress for the active phase: uploads completed, signatures done, overdue items, and tasks assigned to you.",
      when: () => hasSiteSelector,
      onEnter: () => showOverview?.(),
    },
    {
      id: "site-open-tasks",
      target: '[data-tour="isf-site-open-tasks"]',
      placement: "left",
      title: "Open tasks list",
      body: "All pending uploads and signatures for this site appear here. Use Upload or Sign to jump straight to the item. Tasks are only actionable in the current study phase.",
      when: () => hasSiteSelector,
      onEnter: () => showOverview?.(),
    },
    {
      id: "tree",
      target: '[data-tour="isf-folder-tree"]',
      placement: "right",
      title: "Document tree",
      body: "Expand folders with +/−. Milestones and placeholders shown here match the selected study phase. Switch Phase to review what was expected in earlier or later gates without changing live data.",
      when: () => hasTree,
    },
    {
      id: "milestone-dashboard",
      target: '[data-tour="isf-milestone-matrix"]',
      placement: "left",
      title: "Milestone matrix",
      body: "Selecting a milestone opens this dashboard: completeness %, signature overview, and a card matrix for each expected document (upload + sign status). Content reflects the phase you are viewing.",
      when: () => hasMilestoneInTree,
      onEnter: () => {
        selectFirstMilestone?.();
        openStudyInfo?.();
      },
    },
    {
      id: "study-info",
      target: '[data-tour="isf-study-info"]',
      placement: "left",
      title: "Study info panel",
      body: "Study info shows metadata for the selected folder, placeholder, milestone, or document. Expand Milestone to see role and signature matrices.",
      onEnter: () => openStudyInfo?.(),
    },
    {
      id: "study-gates-progress",
      target: '[data-tour="isf-study-gates-progress"]',
      placement: "left",
      title: "Study gates progress",
      body: "Study gates lists completed phases and the current one only. A check inside the badge means finished; the spinner means in progress. To preview upcoming phases, use the Phase picker in the breadcrumb.",
      when: () => hasStudyGate,
      onEnter: () => openStudyInfo?.(),
    },
    {
      id: "role-matrix",
      target: '[data-tour="isf-study-info-matrix"]',
      placement: "left",
      title: "Upload & signature matrix",
      body: "At placeholder or document level, see uploader roles, signature roles, and Done vs Pending signers. Completeness shows upload and sign progress in one bar.",
      when: () => hasTree,
      onEnter: () => {
        selectFirstPlaceholder?.();
        openStudyInfo?.();
      },
    },
    {
      id: "upload-intro",
      placement: "center",
      title: "Upload document",
      body: "Next we preview the upload panel. Choose a file, link an expected placeholder if needed, and enter document metadata before saving. Upload is only available in the current study phase.",
      onEnter: () => openUploadDemo?.(),
      onLeave: () => closeUploadDemo?.(),
    },
    {
      id: "upload-file",
      target: '[data-tour="isf-upload-file"]',
      placement: "left",
      title: "Choose file",
      body: "Drag and drop or browse to select the file. Supported formats depend on your organisation settings.",
      onEnter: () => openUploadDemo?.(),
      onLeave: () => closeUploadDemo?.(),
    },
    {
      id: "upload-metadata",
      target: '[data-tour="isf-upload-metadata"]',
      placement: "left",
      title: "Document metadata",
      body: "Set document type and category (searchable pickers), external document ID, expiry date, optional placeholder link, previous version, and tags.",
      onEnter: () => openUploadDemo?.(),
      onLeave: () => closeUploadDemo?.(),
    },
    {
      id: "upload-actions",
      target: '[data-tour="isf-upload-actions"]',
      placement: "top",
      title: "Upload actions",
      body: "Upload stays disabled until a file and external document ID are provided. It is also disabled when you are previewing a non-current study phase.",
      onEnter: () => openUploadDemo?.(),
      onLeave: () => closeUploadDemo?.(),
    },
    {
      id: "preview",
      target: '[data-tour="isf-preview-panel"]',
      placement: "left",
      title: "Preview panel",
      body: "Uploaded documents open here for preview and signing. When browsing another phase, a read-only banner appears at the top of this panel.",
      onEnter: () => {
        closeUploadDemo?.();
        showOverview?.();
      },
    },
    {
      id: "done",
      placement: "center",
      title: "You are ready",
      body: "Pick your site and current phase, use the tree to find work, check matrices at each level, and upload to fulfill expected documents. Use Phase to review past or future gates without making changes.",
      onEnter: () => cleanupTour?.(),
    },
  ];

  return steps.filter((step) => (step.when ? step.when(context) : true));
}
